import { AppError, requireData, toAppError } from "../../lib/appError";
import { config } from "../../lib/config";
import type { Database } from "../../lib/database.types";
import { supabase } from "../../lib/supabaseClient";

type Asset = Database["public"]["Tables"]["assets"]["Row"];
type AssetUpdate = Database["public"]["Tables"]["assets"]["Update"];

// Uploads and deletes go through the Worker (worker/assets.ts), which stores
// files in R2 and writes the asset row with the caller's token.
const assetsApi = async (path: string, init: RequestInit, fallback: string): Promise<Asset> => {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new AppError("You must be signed in to upload files.", "AUTH_REQUIRED");

  const response = await fetch(`/api/assets${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${token}` },
  }).catch((error: unknown) => {
    throw new AppError(fallback, "NETWORK_ERROR", error);
  });
  const body = (await response.json().catch(() => null)) as { code?: string; message?: string } | null;
  if (response.ok && body) return body as Asset;
  if (body?.code?.startsWith("ASSET_") && body.message) throw new AppError(body.message, body.code, body);
  throw toAppError({ code: body?.code, message: body?.message ?? response.statusText }, fallback);
};

export const assetRepository = {
  async list(siteId: string): Promise<Asset[]> {
    const { data, error } = await supabase.from("assets").select("*").eq("site_id", siteId).order("created_at");
    return requireData(data, error, "Could not load assets.");
  },

  async update(assetId: string, values: AssetUpdate): Promise<Asset> {
    const { data, error } = await supabase.from("assets").update(values).eq("id", assetId).select().single();
    return requireData(data, error, "Could not update the asset.");
  },

  upload(siteId: string, file: File, altText: string): Promise<Asset> {
    const body = new FormData();
    body.set("siteId", siteId);
    body.set("altText", altText);
    body.set("file", file);
    return assetsApi("", { method: "POST", body }, "Could not upload the asset.");
  },

  remove(assetId: string): Promise<Asset> {
    return assetsApi(`/${encodeURIComponent(assetId)}`, { method: "DELETE" }, "Could not delete the asset.");
  },

  getPublicUrl(path: string) {
    return `${config.mediaBaseUrl}/${path.split("/").map(encodeURIComponent).join("/")}`;
  },
};
